import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { MessageSquare } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import { Badge, Button, Card, EmptyState, Inline, Stack, Text } from '../components/atoms';
import { Heading } from '../components/atoms/Typography/Heading';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { AdminArea, useIsAdmin } from '../components/admin/AdminArea';
import { formatDateTime } from '../components/admin/adminFormat';
import { useFeedbackOptional } from '../components/appFeedback/FeedbackContext';
import { fetchAdminFeedback, type AdminFeedbackItem } from '../services/appFeedbackService';
import './AdminFeedbackPage.css';

const PAGE_SIZE = 20;

function FeedbackCard({ item }: { item: AdminFeedbackItem }) {
  const { t, i18n } = useTranslation();
  const sections = [
    { key: 'liked', label: t('appFeedback.liked'), text: item.liked },
    { key: 'problems', label: t('appFeedback.problems'), text: item.problems },
    { key: 'missing', label: t('appFeedback.missing'), text: item.missing },
  ] as const;
  return (
    <Card padding="lg">
      <Stack gap="4">
        <Inline gap="2" align="center" justify="between" wrap>
          <Heading level={3} className="admin-feedback__author">
            {item.userName ?? item.userEmail ?? item.userId}
          </Heading>
          <Inline gap="2" align="center" wrap>
            <Badge tone={item.source === 'prompt' ? 'info' : 'neutral'}>
              {item.source === 'prompt' ? t('adminArea.feedback.sourcePrompt') : t('adminArea.feedback.sourceMenu')}
            </Badge>
            <Text as="span" size="sm" color="muted">
              {formatDateTime(item.createdAt, i18n.language)}
            </Text>
          </Inline>
        </Inline>
        {item.userName && item.userEmail && (
          <Text size="sm" color="secondary">{item.userEmail}</Text>
        )}
        <dl className="admin-feedback__answers">
          {sections.map((section) => (
            <div key={section.key} className="admin-feedback__answer">
              <dt className="admin-feedback__question">{section.label}</dt>
              <dd className="admin-feedback__text">
                {section.text ?? <span className="admin-feedback__none">{t('adminArea.feedback.noAnswer')}</span>}
              </dd>
            </div>
          ))}
        </dl>
      </Stack>
    </Card>
  );
}

/** Admin → Feedback: what users like, the problems they see and what they miss (GET /api/v1/admin/feedback). */
export function AdminFeedbackPage() {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const feedbackDialog = useFeedbackOptional();
  const [offset, setOffset] = useState(0);
  const query = useQuery({
    queryKey: ['admin', 'feedback', offset],
    queryFn: () => fetchAdminFeedback(PAGE_SIZE, offset),
    enabled: isAdmin,
    placeholderData: keepPreviousData,
  });
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader title={t('adminArea.title')} description={t('adminArea.feedback.description')} />
          <AdminArea>
            <Section title={t('adminArea.feedback.listTitle', { count: total })}>
              {query.isLoading && (
                <LiveRegion>
                  <Text>{t('adminArea.feedback.loading')}</Text>
                </LiveRegion>
              )}
              {query.isError && (
                <LoadErrorState title={t('adminArea.feedback.loadError')} onRetry={() => void query.refetch()} />
              )}
              {!query.isLoading && !query.isError && items.length === 0 && (
                <EmptyState
                  icon={<MessageSquare size={48} strokeWidth={1.5} aria-hidden="true" />}
                  headline={t('adminArea.feedback.emptyTitle')}
                  description={t('adminArea.feedback.emptyMessage')}
                  actionLabel={feedbackDialog ? t('adminArea.feedback.emptyAction') : undefined}
                  onAction={feedbackDialog ? feedbackDialog.open : undefined}
                  headingLevel={3}
                />
              )}
              {!query.isLoading && !query.isError && items.length > 0 && (
                <Stack gap="4">
                  <LiveRegion>
                    <Text size="sm" color="muted">{t('adminArea.feedback.range', { from, to, total })}</Text>
                  </LiveRegion>
                  <ul className="admin-feedback__list">
                    {items.map((item) => (
                      <li key={item.id}>
                        <FeedbackCard item={item} />
                      </li>
                    ))}
                  </ul>
                  {total > PAGE_SIZE && (
                    <Inline gap="2" justify="between" wrap>
                      <Button
                        variant="secondary"
                        disabled={offset === 0}
                        onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                      >
                        {t('adminArea.feedback.previous')}
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={offset + PAGE_SIZE >= total}
                        onClick={() => setOffset(offset + PAGE_SIZE)}
                      >
                        {t('adminArea.feedback.next')}
                      </Button>
                    </Inline>
                  )}
                </Stack>
              )}
            </Section>
          </AdminArea>
        </PageContent>
      }
    />
  );
}

export default AdminFeedbackPage;
